import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "i18n"

// Material UI
import { makeStyles } from "@material-ui/core/styles"
import { Grid, Typography } from "@material-ui/core"

// Components
import LearningModesCard from "./LearningModesCard"

// Helpers
import { LEARNING_MODES_SLUG } from "shared/constants"

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: "#F8FAFF",
    padding: theme.spacing(5, 0, 1, 0)
  },
  heading: {
    margin: theme.spacing(0, 0, 7, 0)
  },
  modesWrapper: {
    marginBottom: theme.spacing(6)
  }
}))

const getDescription = name => {
  switch (name) {
    case LEARNING_MODES_SLUG.SELF_PACED:
      return "Consume bite-sized content on the go"
    case LEARNING_MODES_SLUG.BLENDED:
      return "Interact with like-minded people"
    case LEARNING_MODES_SLUG.BLENDED_WITH_CERTIFICATE:
      return "Get International Recognition"

    default:
      return ""
  }
}

const getBaseFeatures = rawFeatures => {
  return rawFeatures.map(obj => ({
    ...obj,
    description: getDescription(obj.learning_mode),
    features: obj.features.map(text => ({
      icon:
        obj.learning_mode === LEARNING_MODES_SLUG.SELF_PACED ? "check" : "add",
      text
    }))
  }))
}

const LearningModes = ({
  prices: products,
  features,
  handlePurchase,
  disablePurchase = false,
  isEnrolled,
  hidePrice,
  buyLoading = false
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [modes, setModes] = useState([])

  useEffect(() => {
    const baseFeatures = getBaseFeatures(features)

    let cache = []
    const formattedFeatures = baseFeatures
      .map((mode, index) => {
        let features = [...mode.features]
        if (cache.length > 0) {
          features = mode.features.filter(({ text }) => !cache.includes(text))
          features.unshift({
            icon: "check",
            text: `All features of ${baseFeatures[index - 1].learning_mode}`
          })
        }
        cache = mode.features.map(({ text }) => text)

        const product = products.find(
          obj => obj.learning_mode === mode.learning_mode
        )

        if (!product) return null

        return {
          ...mode,
          purchaseId: product.id,
          price: product.price,
          features
        }
      })
      .filter(Boolean)

    setModes(formattedFeatures)
  }, [features, products])

  return (
    <Grid
      id="compareLearningModes"
      container
      justifyContent="center"
      classes={{
        container: classes.container
      }}
    >
      <Grid item xs={10}>
        <Typography variant="h3" className={classes.heading}>
          {t("Everyone learns differently")}
        </Typography>

        <Grid
          container
          spacing={3}
          justifyContent="center"
          className={classes.modesWrapper}
        >
          {modes.map(mode => (
            <Grid item sm={12} md={6} lg={4} key={mode.learning_mode}>
              <LearningModesCard
                hidePrice={hidePrice}
                heading={mode.learning_mode}
                description={mode.description}
                price={mode.price}
                handlePurchase={() => handlePurchase(mode.purchaseId)}
                features={mode.features}
                canBuy={
                  !disablePurchase &&
                  mode.learning_mode === LEARNING_MODES_SLUG.SELF_PACED
                }
                recommended={
                  mode.learning_mode ===
                  LEARNING_MODES_SLUG.BLENDED_WITH_CERTIFICATE
                }
                subjectQualificationRequired={
                  mode.learning_mode ===
                  LEARNING_MODES_SLUG.BLENDED_WITH_CERTIFICATE
                }
                isEnrolled={isEnrolled}
                buyLoading={buyLoading}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default LearningModes

LearningModes.propTypes = {
  prices: PropTypes.array,
  features: PropTypes.array,
  handlePurchase: PropTypes.func,
  disablePurchase: PropTypes.bool,
  isEnrolled: PropTypes.bool,
  hidePrice: PropTypes.bool
}
